import React, { FC, useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  GetDailyStandupSettingsResponse,
  UpdateDailyStandupSettingsRequest,
} from '@dreamai/types';
import api from '../../api';
import './daily-briefing-section.scss';

// Delivery days (ISO weekday, 1 = Monday … 7 = Sunday; null = every day) are
// likewise newer than the published @dreamai/types; mirror them locally until
// the package is bumped.
type DailyStandupDeliveryDay = 1 | 2 | 3 | 4 | 5 | 6 | 7;
type SettingsResponseWithDays = Omit<
  GetDailyStandupSettingsResponse,
  'settings'
> & {
  settings?: NonNullable<GetDailyStandupSettingsResponse['settings']> & {
    deliveryDays?: DailyStandupDeliveryDay[] | null;
  };
};
type UpdateSettingsWithDays = UpdateDailyStandupSettingsRequest & {
  deliveryDays?: DailyStandupDeliveryDay[] | null;
};

const DAYS: { day: DailyStandupDeliveryDay; short: string; long: string }[] = [
  { day: 1, short: 'Mon', long: 'Monday' },
  { day: 2, short: 'Tue', long: 'Tuesday' },
  { day: 3, short: 'Wed', long: 'Wednesday' },
  { day: 4, short: 'Thu', long: 'Thursday' },
  { day: 5, short: 'Fri', long: 'Friday' },
  { day: 6, short: 'Sat', long: 'Saturday' },
  { day: 7, short: 'Sun', long: 'Sunday' },
];
const ALL_DAYS: DailyStandupDeliveryDay[] = DAYS.map((d) => d.day);
const WEEKDAYS: DailyStandupDeliveryDay[] = [1, 2, 3, 4, 5];

const sameDays = (a: DailyStandupDeliveryDay[], b: DailyStandupDeliveryDay[]) =>
  a.length === b.length && a.every((day, i) => day === b[i]);

const describeDays = (days: DailyStandupDeliveryDay[]): string => {
  if (sameDays(days, ALL_DAYS)) return 'Delivered every day.';
  if (sameDays(days, WEEKDAYS)) return 'Delivered on weekdays only.';
  const names = DAYS.filter((d) => days.includes(d.day)).map((d) => d.long);
  if (names.length === 1) return `Delivered on ${names[0]} only.`;
  return `Delivered on ${names.slice(0, -1).join(', ')} and ${
    names[names.length - 1]
  }.`;
};

const DailyBriefingSection: FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(true);
  const [savingEnabled, setSavingEnabled] = useState(false);
  const [callTime, setCallTime] = useState('07:30');
  const [savedCallTime, setSavedCallTime] = useState('07:30');
  const [savingTime, setSavingTime] = useState(false);
  const [deliveryDays, setDeliveryDays] =
    useState<DailyStandupDeliveryDay[]>(ALL_DAYS);
  const [savingDays, setSavingDays] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const headers = { Authorization: `Bearer ${token}` };
        const settingsRes = await api.get<SettingsResponseWithDays>(
          '/daily-standup/settings',
          { headers },
        );
        if (cancelled) return;
        if (settingsRes.data.success && settingsRes.data.settings) {
          const settings = settingsRes.data.settings;
          const time = settings.callTime.slice(0, 5);
          setCallTime(time);
          setSavedCallTime(time);
          setEnabled(settings.enabled);
          setDeliveryDays(
            settings.deliveryDays && settings.deliveryDays.length > 0
              ? [...settings.deliveryDays].sort((a, b) => a - b)
              : ALL_DAYS,
          );
        }
      } catch (err) {
        console.error('Error fetching daily briefing settings:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently]);

  const putSettings = useCallback(
    async (body: UpdateSettingsWithDays) => {
      const token = await getAccessTokenSilently();
      await api.put('/daily-standup/settings', body, {
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    [getAccessTokenSilently],
  );

  const handleToggleEnabled = useCallback(async () => {
    const next = !enabled;
    setEnabled(next);
    setSavingEnabled(true);
    try {
      await putSettings({ enabled: next });
    } catch (err) {
      console.error('Error updating daily briefing on/off:', err);
      setEnabled(!next);
    } finally {
      setSavingEnabled(false);
    }
  }, [enabled, putSettings]);

  const handleTimeBlur = useCallback(async () => {
    if (!callTime || callTime === savedCallTime) return;
    setSavingTime(true);
    try {
      await putSettings({ callTime });
      setSavedCallTime(callTime);
    } catch (err) {
      console.error('Error updating briefing delivery time:', err);
      setCallTime(savedCallTime);
    } finally {
      setSavingTime(false);
    }
  }, [callTime, savedCallTime, putSettings]);

  const saveDays = useCallback(
    async (next: DailyStandupDeliveryDay[]) => {
      const previous = deliveryDays;
      setDeliveryDays(next);
      setSavingDays(true);
      try {
        await putSettings({
          deliveryDays: sameDays(next, ALL_DAYS) ? null : next,
        });
      } catch (err) {
        console.error('Error updating briefing delivery days:', err);
        setDeliveryDays(previous);
      } finally {
        setSavingDays(false);
      }
    },
    [deliveryDays, putSettings],
  );

  const handleToggleDay = useCallback(
    (day: DailyStandupDeliveryDay) => {
      const selected = deliveryDays.includes(day);
      if (selected && deliveryDays.length === 1) return;
      const next = selected
        ? deliveryDays.filter((d) => d !== day)
        : [...deliveryDays, day].sort((a, b) => a - b);
      void saveDays(next);
    },
    [deliveryDays, saveDays],
  );

  const isWeekdaysOnly = sameDays(deliveryDays, WEEKDAYS);
  const isEveryDay = sameDays(deliveryDays, ALL_DAYS);

  return (
    <div className='daily-briefing-section'>
      <div className='daily-briefing-section-header'>
        <h2 className='section-title'>Daily Briefing</h2>
        <p className='daily-briefing-section-subtitle'>
          Adjust when you get your Daily Briefing, and how it's delivered.
        </p>
      </div>

      <div className='daily-briefing-section-card'>
        {loading ? (
          <p className='daily-briefing-section-loading'>Loading...</p>
        ) : (
          <>
            <div className='daily-briefing-section-row'>
              <div className='daily-briefing-section-row-info'>
                <span className='daily-briefing-section-row-label'>
                  Daily Briefing
                </span>
                <span className='daily-briefing-section-row-status'>
                  {enabled
                    ? 'On — your briefing is prepared and sent on the schedule below.'
                    : 'Your briefing is off. Nothing will be prepared or sent until you turn it back on.'}
                </span>
              </div>
              <button
                className={`toggle-switch${enabled ? ' on' : ''}`}
                onClick={handleToggleEnabled}
                disabled={savingEnabled}
                aria-label='Daily Briefing'
                role='switch'
                aria-checked={enabled}
              >
                <span className='toggle-knob' />
              </button>
            </div>

            {enabled && (
              <>
                <div className='daily-briefing-section-row'>
                  <div className='daily-briefing-section-row-info'>
                    <span className='daily-briefing-section-row-label'>
                      Delivery time
                    </span>
                    <span className='daily-briefing-section-row-status'>
                      Your briefing arrives at this time on each delivery day.
                    </span>
                  </div>
                  <input
                    type='time'
                    className='daily-briefing-section-time-input'
                    value={callTime}
                    onChange={(e) => setCallTime(e.target.value)}
                    onBlur={handleTimeBlur}
                    disabled={savingTime}
                    aria-label='Briefing delivery time'
                  />
                </div>

                <div className='daily-briefing-section-row daily-briefing-section-days-row'>
                  <div className='daily-briefing-section-row-info'>
                    <span className='daily-briefing-section-row-label'>
                      Delivery days
                    </span>
                    <span className='daily-briefing-section-row-status'>
                      {describeDays(deliveryDays)}
                    </span>
                  </div>
                  <div className='daily-briefing-section-days'>
                    <div
                      className='daily-briefing-section-day-chips'
                      role='group'
                      aria-label='Delivery days'
                    >
                      {DAYS.map(({ day, short, long }) => {
                        const selected = deliveryDays.includes(day);
                        return (
                          <button
                            key={day}
                            type='button'
                            role='checkbox'
                            aria-checked={selected}
                            aria-label={short}
                            title={long}
                            className={`daily-briefing-section-day-chip${
                              selected ? ' selected' : ''
                            }`}
                            onClick={() => handleToggleDay(day)}
                            disabled={savingDays}
                          >
                            {short}
                          </button>
                        );
                      })}
                    </div>
                    <div className='daily-briefing-section-day-presets'>
                      <button
                        type='button'
                        className={`daily-briefing-section-day-preset${
                          isWeekdaysOnly ? ' active' : ''
                        }`}
                        onClick={() => void saveDays(WEEKDAYS)}
                        disabled={savingDays || isWeekdaysOnly}
                      >
                        Weekdays only
                      </button>
                      <button
                        type='button'
                        className={`daily-briefing-section-day-preset${
                          isEveryDay ? ' active' : ''
                        }`}
                        onClick={() => void saveDays(ALL_DAYS)}
                        disabled={savingDays || isEveryDay}
                      >
                        Every day
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DailyBriefingSection;
